import { createTranslator, type useTranslations } from "next-intl"
import english from "@/translations/en/quality-learning.json"

import { formatShortDate } from "@/lib/conversation/time-format"

import type { SelfLearningMode } from "./self-learning"
import { MAX_CHANGE_LINE_LENGTH } from "./suggestions/schema"

/** Every customer-facing word about the Self-learning setting. Client-safe. */

/** The text a person's accept edits, against the agent's limit. */
export interface InstructionsRoom {
  length: number;
  limit: number;
}

export interface SelfLearningCopy {
  label: string;
  description: string;
  recommended?: boolean;
  warning?: { tone: "caution" | "danger"; text: string };
}

/** Whom the Overview card speaks of: the selected agent, or a tenant's only one, else every agent. */
export type Learner = "agent" | "agents"


const englishTranslator = createTranslator({locale: "en", messages: english, namespace: "QualityLearning"})

export const { SELF_LEARNING_LABEL, RECOMMENDED_BADGE, SELF_LEARNING_COPY, ARTICLES_OPTION_COPY, IN_EVERY_MODE_NOTE, NO_OWN_INSTRUCTIONS_LINE, instructionsRoomLabel, instructionsRoomNote, instructionsFull, roomLines, learningSinceLine, learningHeading, agentsLearningLine, noChangesLine, FULL_AUTOPILOT_CONFIRM, fullAutopilotTitle } = getQualityLearningFormatters()

export function getQualityLearningFormatters(
  t: ReturnType<typeof useTranslations<"QualityLearning">> = englishTranslator,
  locale = "en",
) {
  const SELF_LEARNING_LABEL = t("selfLearning")

  const RECOMMENDED_BADGE = t("recommended")

  const SELF_LEARNING_COPY: Record<SelfLearningMode, SelfLearningCopy> = {
    off: {
      label: t("off"),
      description:
        t("learnsNothingNewAndClosesTheSuggestionsWaiting"),
    },
    suggest: {
      label: t("suggestOnly"),
      description: t("preparesImprovementsEachOneWaitsForYourOK"),
    },
    automatic: {
      label: t("automatic"),
      recommended: true,
      description:
        t("improvesItsInstructionsAndItsKnowledgeBaseBy"),
    },
    extended: {
      label: t("extended"),
      description:
        t("alsoRewritesTextYouWroteAndAlwaysAdds"),
      warning: { tone: "caution", text: t("mayRewordInstructionsYouWroteYourself") },
    },
    full: {
      label: t("fullAutopilot"),
      description:
        t("decidesEverythingByItselfIncludingRulesLikeDiscounts"),
      warning: {
        tone: "danger",
        text: t("mayChangeHowTheAgentHandlesDiscountsRefunds"),
      },
    },
  }

  const ARTICLES_OPTION_COPY = {
    label: t("addWhatItLearnsToTheKnowledgeBase"),
    off: t("newFactsWaitForYourOK"),
  } as const

  const IN_EVERY_MODE_NOTE =
    t("inEveryModeEachChangeIsCheckedAnd")

  const NO_OWN_INSTRUCTIONS_LINE = t("actsAsSuggestOnlyUntilThisAgentHas")

  function instructionsRoomLabel({ length, limit }: InstructionsRoom): string {
    return t("value0OfValue1Characters", { value0: String(length.toLocaleString(locale)), value1: String(limit.toLocaleString(locale)) })
  }

  function instructionsRoomNote(room: InstructionsRoom): string {
    const lines = roomLines(room)
    if (lines === 0) return t("fullNewLinesCanTBeAddedUntil")
    return t("roomForMoreLines", {count: lines})
  }

  function instructionsFull(room: InstructionsRoom): boolean {
    return roomLines(room) === 0
  }

  function roomLines({ length, limit }: InstructionsRoom): number {
    return Math.max(0, Math.floor((limit - length) / MAX_CHANGE_LINE_LENGTH))
  }

  function learningSinceLine(since: string, timezone: string): string {
    return t("learningSinceValue0", { value0: String(formatShortDate(since, timezone, locale)) })
  }

  function learningHeading(learner: Learner, learning: boolean): string {
    switch (learner) {
      case "agent":
        return learning ? t("yourAgentIsLearning") : t("yourAgentIsnTLearning")
      case "agents":
        return learning ? t("yourAgentsAreLearning") : t("yourAgentsArenTLearning")
      default: {
        const exhaustive: never = learner
        return exhaustive
      }
    }
  }

  function agentsLearningLine(learning: number, total: number): string {
    return t("selfLearningOnForValue0OfValue1Agents", { value0: String(learning.toLocaleString(locale)), value1: String(total.toLocaleString(locale)) })
  }

  function noChangesLine(learner: Learner, learning: boolean): string {
    switch (learner) {
      case "agent":
        return learning
          ? t("noChangesYetTheyAppearHereAsThe")
          : t("noChangesYetTheAgentLearnsNothingNew")
      case "agents":
        return learning
          ? t("noChangesYetTheyAppearHereAsYour")
          : t("noChangesYetYourAgentsLearnNothingNew")
      default: {
        const exhaustive: never = learner
        return exhaustive
      }
    }
  }

  const FULL_AUTOPILOT_CONFIRM = {
    body:
      t("theAgentWillChangeItsInstructionsWithoutAsking"),
    protectionsHeading: t("whatStillProtectsYou"),
    protections: [
      t("eachChangeIsCheckedAndTakenBackAutomatically"),
      t("everyChangeIsListedUnderChangesWithUndo"),
      t("aChangeYouUndoIsNeverMadeAgain"),
    ],
    acknowledgement: t("iUnderstandTheAgentMayChangeTheseRules"),
    confirm: t("turnOnFullAutopilot"),
  } as const

  function fullAutopilotTitle(agentName: string): string {
    return t("turnOnFullAutopilotForValue0", { value0: String(agentName) })
  }

  return { SELF_LEARNING_LABEL, RECOMMENDED_BADGE, SELF_LEARNING_COPY, ARTICLES_OPTION_COPY, IN_EVERY_MODE_NOTE, NO_OWN_INSTRUCTIONS_LINE, instructionsRoomLabel, instructionsRoomNote, instructionsFull, roomLines, learningSinceLine, learningHeading, agentsLearningLine, noChangesLine, FULL_AUTOPILOT_CONFIRM, fullAutopilotTitle }
}
